import React, { useState } from "react";
import { ChevronDown, ChevronRight, History, Clock3, Trash2, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

const money = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatCst = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString("en-US", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const ACTION_STYLES = {
  create: "bg-emerald-100 text-emerald-800",
  update: "bg-blue-100 text-blue-800",
  delete: "bg-red-100 text-red-800",
};

const ACTION_LABELS = {
  create: "Added",
  update: "Updated",
  delete: "Deleted",
};

/**
 * Admin/Management activity trail for Waitress Tips create / update / delete.
 * Admin can clear logs for the current date range.
 */
export function WaitressTipActivityLogPanel({
  logs = [],
  isLoading = false,
  title = "Record Change Activity Log",
  canClear = false,
  onClearLogs,
  clearing = false,
}) {
  const [open, setOpen] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const handleClear = async (e) => {
    e.stopPropagation();
    if (!onClearLogs || clearing || logs.length === 0) return;
    const confirmed = window.confirm(
      `Clear ${logs.length} activity log${logs.length === 1 ? "" : "s"} for the selected date range? This cannot be undone.`
    );
    if (!confirmed) return;
    await onClearLogs();
  };

  return (
    <div className="mt-6 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex w-full items-center justify-between bg-gray-50 px-5 py-4">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex flex-1 items-center gap-3 text-left transition hover:opacity-90"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-yellow-200 bg-yellow-100 text-yellow-800">
            <History className="h-4 w-4" />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-800">{title}</p>
            <p className="text-xs text-gray-500">
              Admin/Management view of all waitress tip adds, updates, and deletes — including Admin&apos;s own actions.
            </p>
          </div>
          <span className="rounded-full bg-yellow-400 px-2.5 py-0.5 text-xs font-bold text-black">
            {logs.length}
          </span>
        </button>

        <div className="ml-3 flex items-center gap-2">
          {canClear && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClear}
              disabled={clearing || logs.length === 0 || isLoading}
              className="border-red-200 text-red-700 hover:bg-red-50"
            >
              {clearing ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="mr-1 h-3.5 w-3.5" />
              )}
              Clear Logs
            </Button>
          )}
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className="rounded p-1 text-gray-500 hover:bg-gray-100"
            aria-label={open ? "Collapse activity log" : "Expand activity log"}
          >
            {open ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-100 px-5 pb-5 pt-2">
          {isLoading ? (
            <p className="py-4 text-sm text-gray-500">Loading activity logs…</p>
          ) : logs.length === 0 ? (
            <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center">
              <p className="text-sm text-gray-500">
                No activity yet for this date range. Add, edit, or delete a
                waitress tip record to create the first log.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="px-2 py-2 font-medium">When</th>
                    <th className="px-2 py-2 font-medium">Action</th>
                    <th className="px-2 py-2 font-medium">Performed By</th>
                    <th className="px-2 py-2 font-medium">Record</th>
                    <th className="px-2 py-2 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map((log) => {
                    const isExpanded = expandedId === log.id;
                    const action = String(log.action || "").toLowerCase();
                    return (
                      <React.Fragment key={log.id}>
                        <tr
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() =>
                            setExpandedId(isExpanded ? null : log.id)
                          }
                        >
                          <td className="px-2 py-3 whitespace-nowrap text-gray-600">
                            <span className="inline-flex items-center gap-1">
                              <Clock3 className="h-3 w-3" />
                              {formatCst(log.created_at)}
                            </span>
                          </td>
                          <td className="px-2 py-3">
                            <span
                              className={`rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
                                ACTION_STYLES[action] || "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {ACTION_LABELS[action] || action || "Change"}
                            </span>
                          </td>
                          <td className="px-2 py-3 text-gray-800">
                            {log.performed_by || "Unknown"}
                            {log.performed_by_role ? (
                              <span className="ml-1 text-xs text-gray-500">
                                ({log.performed_by_role})
                              </span>
                            ) : null}
                          </td>
                          <td className="px-2 py-3 text-gray-700">
                            {log.record_id != null ? `#${log.record_id}` : "—"}
                            {log.employee ? `(${log.employee})` : ""}
                          </td>
                          <td className="px-2 py-3 text-gray-700">
                            {log.summary ||
                              `${log.performed_by || "Unknown"} ${action}d a tip record`}
                          </td>
                        </tr>
                        {isExpanded && (log.before_snapshot || log.after_snapshot) && (
                          <tr>
                            <td colSpan={5} className="bg-yellow-50 px-3 py-3">
                              <div className="grid gap-3 text-xs text-gray-700 sm:grid-cols-2">
                                {log.before_snapshot && (
                                  <div>
                                    <p className="mb-1 font-semibold text-gray-800">Before</p>
                                    <p>Employee: {log.before_snapshot.employee || "—"}</p>
                                    <p>Tip: ${money(log.before_snapshot.tip_out_cash)}</p>
                                    <p>Tax: ${money(log.before_snapshot.tax)}</p>
                                    <p>Owe to Toast: ${money(log.before_snapshot.owe_to_toast)}</p>
                                    <p>Added by: {log.before_snapshot.added_by || "—"}</p>
                                  </div>
                                )}
                                {log.after_snapshot && (
                                  <div>
                                    <p className="mb-1 font-semibold text-gray-800">After</p>
                                    <p>Employee: {log.after_snapshot.employee || "—"}</p>
                                    <p>Tip: ${money(log.after_snapshot.tip_out_cash)}</p>
                                    <p>Tax: ${money(log.after_snapshot.tax)}</p>
                                    <p>Owe to Toast: ${money(log.after_snapshot.owe_to_toast)}</p>
                                    <p>Updated by: {log.after_snapshot.updated_by || "—"}</p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
